import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import type { HomeWidgetProps } from '@/lib/home-widgets'
import { HomeWidgetEmpty, HomeWidgetError, HomeWidgetLoading } from '@/ui/home-widget'
import { RelativeTime } from '@/ui/relative-time'

import { useRecentActivity } from './recent-activity-queries'

export function RecentActivityWidget({ entityId }: HomeWidgetProps) {
  const userId = useAuth().user?.userId
  if (!userId) return <HomeWidgetLoading />
  return <RecentActivityFeed key={userId} entityId={entityId} userId={userId} />
}

function RecentActivityFeed({ entityId, userId }: { entityId: string; userId: string }) {
  const seenKey = `recent-activity-seen:${userId}:${entityId}`
  const [seenUntilMount] = useState(() => parseTime(localStorage.getItem(seenKey)))
  const query = useRecentActivity(entityId)

  const newest = query.data?.items[0]?.values.timestamp
  useEffect(() => {
    if (newest && parseTime(newest) > parseTime(localStorage.getItem(seenKey)))
      localStorage.setItem(seenKey, newest)
  }, [seenKey, newest])

  if (query.isPending) return <HomeWidgetLoading />
  if (query.isError) return <HomeWidgetError onRetry={() => void query.refetch()} />
  if (query.data.items.length === 0) return <HomeWidgetEmpty>No recent activity.</HomeWidgetEmpty>

  return (
    <ul className="divide-border -my-2 flex flex-col divide-y">
      {query.data.items.map(({ key, values }) => {
        const unread = parseTime(values.timestamp) > seenUntilMount
        return (
          <li key={key} className="flex items-start gap-2 py-2 text-sm">
            <span aria-hidden className={`mt-1.5 size-2 shrink-0 rounded-full ${unread ? 'bg-primary' : 'bg-transparent'}`} />
            <div className="min-w-0 flex-1">
              <p className={unread ? 'font-semibold' : undefined}>
                {unread && <span className="sr-only">Unread: </span>}
                {values.actor} <span aria-hidden>·</span> {values.details}
              </p>
              {values.entity && <p className="text-muted-foreground text-xs break-words">{values.entity}</p>}
            </div>
            {values.timestamp && (
              <span className="text-muted-foreground shrink-0 text-xs">
                <RelativeTime iso={values.timestamp} />
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function parseTime(iso: string | null | undefined) {
  return iso ? Date.parse(iso) || 0 : 0
}
